/**
 * Map preview + encounter UI: compare an enemy type's core stats to the canonical
 * average enemy baseline at the encounter level (not vs same-type averages).
 */

export interface EnemyCoreStats {
	attack: number;
	defence: number;
	hitpoints: number;
	manaPoints: number;
	accuracy: number;
	evasion: number;
	speed: number;
	critical: number;
}

/** Prisma `EnemyType` defaults — generic average enemy at level 1. */
export const AVERAGE_ENEMY_TYPE_BASE_STATS: EnemyCoreStats = {
	attack: 10,
	defence: 10,
	hitpoints: 100,
	manaPoints: 100,
	accuracy: 10,
	evasion: 10,
	speed: 10,
	critical: 10,
};

export const ENEMY_STAT_PROFILE_STAT_KEYS = [
	"attack",
	"defence",
	"accuracy",
	"evasion",
	"speed",
	"critical",
] as const;

export type EnemyStatProfileStatKey =
	(typeof ENEMY_STAT_PROFILE_STAT_KEYS)[number];

export interface EnemyStatComparisonEntry {
	stat: EnemyStatProfileStatKey;
	/** Signed proportional delta vs level-scaled average (e.g. 0.4 = +40%). */
	proportionalDelta: number;
}

export interface EnemyStatStrengthsWeaknesses {
	strengths: EnemyStatComparisonEntry[];
	weaknesses: EnemyStatComparisonEntry[];
}

/** Minimum |delta| vs level-scaled average to count as a strength or weakness (0.2 = ±20%). */
export const DEFAULT_ENEMY_STAT_PROFILE_MIN_PROPORTIONAL_DELTA = 0.2;

export function scaleEnemyCoreStatsByLevel(
	base: EnemyCoreStats,
	level: number,
): EnemyCoreStats {
	const l = Math.max(1, Math.floor(level));
	return {
		attack: Math.round(base.attack * l),
		defence: Math.round(base.defence * l),
		hitpoints: Math.round(base.hitpoints * l),
		manaPoints: Math.round(base.manaPoints * l),
		accuracy: Math.round(base.accuracy * l),
		evasion: Math.round(base.evasion * l),
		speed: Math.round(base.speed * l),
		critical: Math.round(base.critical * l),
	};
}

/**
 * Blend type baseline with rolled instance stats so preview matches battle with minor variance.
 */
export function blendEnemyCoreStatsWithInstance(
	typeStats: EnemyCoreStats,
	instanceStats: EnemyCoreStats,
	instanceWeight = 0.15,
): EnemyCoreStats {
	const w = Math.max(0, Math.min(1, instanceWeight));
	const tw = 1 - w;
	const blend = (key: keyof EnemyCoreStats) =>
		Math.round(typeStats[key] * tw + instanceStats[key] * w);
	return {
		attack: blend("attack"),
		defence: blend("defence"),
		hitpoints: blend("hitpoints"),
		manaPoints: blend("manaPoints"),
		accuracy: blend("accuracy"),
		evasion: blend("evasion"),
		speed: blend("speed"),
		critical: blend("critical"),
	};
}

export function resolveEnemyStatStrengthsAndWeaknesses(params: {
	enemyStats: EnemyCoreStats;
	level: number;
	maxEach?: number;
	/** Min proportional gap vs average (default ±20%). */
	minProportionalDelta?: number;
}): EnemyStatStrengthsWeaknesses {
	const maxEach = params.maxEach ?? 3;
	const minDelta =
		params.minProportionalDelta ??
		DEFAULT_ENEMY_STAT_PROFILE_MIN_PROPORTIONAL_DELTA;
	const level = Math.max(1, Math.floor(params.level));
	const averageAtLevel = scaleEnemyCoreStatsByLevel(
		AVERAGE_ENEMY_TYPE_BASE_STATS,
		level,
	);
	const enemyAtLevel = scaleEnemyCoreStatsByLevel(params.enemyStats, level);

	const strengths: EnemyStatComparisonEntry[] = [];
	const weaknesses: EnemyStatComparisonEntry[] = [];

	for (const stat of ENEMY_STAT_PROFILE_STAT_KEYS) {
		const averageValue = averageAtLevel[stat];
		const enemyValue = enemyAtLevel[stat];
		if (averageValue <= 0) {
			continue;
		}
		const proportionalDelta = (enemyValue - averageValue) / averageValue;
		if (proportionalDelta >= minDelta) {
			strengths.push({ stat, proportionalDelta });
		} else if (proportionalDelta <= -minDelta) {
			weaknesses.push({ stat, proportionalDelta });
		}
	}

	strengths.sort((a, b) => b.proportionalDelta - a.proportionalDelta);
	weaknesses.sort((a, b) => a.proportionalDelta - b.proportionalDelta);

	return {
		strengths: strengths.slice(0, maxEach),
		weaknesses: weaknesses.slice(0, maxEach),
	};
}
